import { Box, Dialog, Flex, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useRef, useState } from 'react';

import { Button, MicOffIcon } from '#features/ui';
import { DialogContent, DialogRoot } from '#features/ui/components/Dialog';

/** Действия над участником, требующие подтверждения: оба необратимы для чужого разговора. */
export type VoiceModerationAction = 'muteForAll' | 'remove';

type VoiceModerationDialogProps = {
  action: VoiceModerationAction | null;
  displayName: string;
  avatar?: React.ReactNode;
  isBusy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

/**
 * Подтверждение перед мьютом для всех и удалением из голоса. Одно случайное нажатие не должно
 * выкидывать человека из разговора посреди катки.
 *
 * Красная кнопка — только у необратимого действия. Мьют снимается тем же меню, поэтому его
 * подтверждение выглядит обычным.
 */
export const VoiceModerationDialog = ({
  action,
  displayName,
  avatar,
  isBusy,
  onConfirm,
  onClose,
}: VoiceModerationDialogProps) => {
  const { t } = useLingui();
  // Фокус при открытии — на отмене: подтверждение не должно срабатывать вслепую по Enter.
  const cancelRef = useRef<HTMLButtonElement>(null);

  const isRemove = action === 'remove';

  return (
    <DialogRoot
      open={Boolean(action)}
      onOpenChange={({ open }) => (open ? undefined : onClose())}
      initialFocusEl={() => cancelRef.current}
      role="alertdialog"
    >
      <DialogContent maxW="440px">
        <Dialog.Body p={5} display="flex" flexDirection="column" gap={3.5}>
          <Flex align="center" gap={3}>
            {avatar}
            <Text textStyle="md" fontWeight={700} lineHeight={1.3}>
              {isRemove
                ? `${t`post:voice.moderation.removeTitle`} ${displayName}?`
                : `${t`post:voice.moderation.muteForAllTitle`} ${displayName}?`}
            </Text>
          </Flex>

          <Text textStyle="sm" color="fg.secondary" lineHeight={1.5}>
            {isRemove
              ? t`post:voice.moderation.removeDescription`
              : t`post:voice.moderation.muteForAllDescription`}
          </Text>

          <Flex gap={2.5} justify="flex-end">
            <Button ref={cancelRef} variant="outline" onClick={onClose}>
              {t`post:voice.room.cancel`}
            </Button>
            <Button
              colorPalette={isRemove ? 'red' : undefined}
              bgColor={isRemove ? 'red.500' : undefined}
              color={isRemove ? 'bg.inverted' : undefined}
              loading={isBusy}
              onClick={onConfirm}
            >
              {isRemove
                ? t`post:voice.moderation.removeConfirm`
                : t`post:voice.moderation.muteConfirm`}
            </Button>
          </Flex>
        </Dialog.Body>
      </DialogContent>
    </DialogRoot>
  );
};

/**
 * Состояние заглушённого участника: микрофон недоступен, и сказано, кто именно его выключил —
 * иначе человек решит, что сломался интерфейс, и будет чинить микрофон в системе.
 */
export const VoiceMutedByHostNotice = () => {
  const { t } = useLingui();

  return (
    <Flex align="center" gap={3} bg="bg.surface" borderRadius="xl" boxShadow="md" px={3.5} py={3}>
      <Flex
        w="44px"
        h="44px"
        flex="none"
        borderRadius="lg"
        bg="bg.error"
        color="red.400"
        align="center"
        justify="center"
        opacity={0.5}
      >
        <MicOffIcon fontSize="19px" />
      </Flex>
      <Box>
        <Text textStyle="label">{t`post:voice.moderation.mutedByHost`}</Text>
        <Text textStyle="xs" color="fg.secondary" mt={0.5}>
          {t`post:voice.moderation.mutedByHostDetail`}
        </Text>
      </Box>
    </Flex>
  );
};

/** Пятисекундное окно отмены после удаления — обычный сценарий «нажал не на того». */
export const useVoiceRemovalUndo = (onUndo: (userId: string) => void) => {
  const [pending, setPending] = useState<{ userId: string; displayName: string } | null>(null);
  const timerRef = useRef<number | null>(null);

  const start = (userId: string, displayName: string) => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }
    setPending({ userId, displayName });
    timerRef.current = window.setTimeout(() => setPending(null), 5000);
  };

  const undo = () => {
    if (!pending) {
      return;
    }
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }
    onUndo(pending.userId);
    setPending(null);
  };

  return { pending, start, undo };
};
